import { Popover, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { type NutritionProfileFragment } from "../gql/generated/graphqlSchema.tsx";
import { formatNutrient } from "../utils/nutritionFormat.ts";
import { NutritionInfo } from "./NutritionInfo.tsx";

type NutritionSummaryProps = {
  readonly name: string;
  readonly nutritionProfile: NutritionProfileFragment;
  readonly showTooltip?: boolean;
};

export const NutritionSummary = ({ name, nutritionProfile: np, showTooltip = false }: NutritionSummaryProps) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const parts: string[] = [];
  const kj = formatNutrient(np.energyKj, "energy", lang);
  const kcal = formatNutrient(np.energyKcal, "energy", lang);
  if (kj != null || kcal != null) {
    const energyParts = [kj != null ? `${kj} kJ` : "", kcal != null ? `${kcal} kcal` : ""].filter(Boolean);
    parts.push(energyParts.join(" / "));
  }
  const fat = formatNutrient(np.fat, "fat", lang);
  if (fat != null) {
    const satFat = formatNutrient(np.saturatedFat, "saturatedFat", lang);
    const satSuffix = satFat != null ? ` (${t("recipes.nutrition.saturatedFatShort")} ${satFat} g)` : "";
    parts.push(`${t("recipes.nutrition.fat")} ${fat} g${satSuffix}`);
  }
  const carb = formatNutrient(np.carbohydrate, "carbohydrate", lang);
  if (carb != null) {
    const sugars = formatNutrient(np.sugars, "sugars", lang);
    const sugarsSuffix = sugars != null ? ` (${t("recipes.nutrition.sugarsShort")} ${sugars} g)` : "";
    parts.push(`${t("recipes.nutrition.carbohydrate")} ${carb} g${sugarsSuffix}`);
  }
  const protein = formatNutrient(np.protein, "protein", lang);
  if (protein != null) parts.push(`${t("recipes.nutrition.protein")} ${protein} g`);
  const salt = formatNutrient(np.salt, "salt", lang);
  if (salt != null) parts.push(`${t("recipes.nutrition.salt")} ${salt} g`);

  if (parts.length === 0) return null;

  return (
    <Popover
      content={<NutritionInfo nutritionProfile={np} />}
      destroyOnHidden
      mouseEnterDelay={0.5}
      open={showTooltip ? undefined : false}
      title={name}
    >
      <Typography.Text type="secondary">
        <small>{parts.join(" · ")}</small>
      </Typography.Text>
    </Popover>
  );
};
